import { type JSX, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Icon } from '@/components/icons';
import { Button, Input, Spinner } from '@/components/ui';
import { api, ApiError } from '@/lib/api';

/**
 * Public self-service sign-up page (/signup). Posts to /v1/auth/signup, which
 * records a PENDING request — no account is provisioned until a platform admin
 * approves it. So we don't log the user in; we show a "pending review"
 * confirmation instead.
 */

const schema = z
  .object({
    fullName: z.string().min(1, 'Your name is required'),
    email: z.string().email('Enter a valid email'),
    company: z.string().max(200).optional().or(z.literal('')),
    phone: z.string().min(5, 'Enter a phone we can reach you at').max(40),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(8, 'Confirm your password'),
    // Honeypot — stays empty for real users (the field is visually hidden).
    // Passed through to the API, which drops the submission if it's filled.
    website: z.string().max(200).optional().or(z.literal('')),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

export default function SignupPage(): JSX.Element {
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      fullName: '',
      email: '',
      company: '',
      phone: '',
      password: '',
      confirmPassword: '',
      website: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setErrorMsg(null);
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (typeof path === 'string') setError(path as keyof FormValues, { message: issue.message });
      }
      return;
    }
    try {
      await api.post('/v1/auth/signup', {
        full_name: parsed.data.fullName,
        email: parsed.data.email,
        company: parsed.data.company || undefined,
        phone: parsed.data.phone,
        password: parsed.data.password,
        website: parsed.data.website || '',
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'already_pending') {
          setErrorMsg('A request for this email is already awaiting review. Hang tight — we’ll be in touch.');
        } else if (err.code === 'invalid_input') {
          setErrorMsg(err.message);
        } else {
          setErrorMsg('Something went wrong. Please try again in a moment.');
        }
      } else {
        setErrorMsg('Network error. Check your connection and retry.');
      }
    }
  });

  if (done) {
    return (
      <div className="w-full min-h-screen grid place-items-center bg-paper px-6">
        <div className="w-full max-w-[440px] text-center">
          <div className="w-12 h-12 rounded-full bg-volt-50 grid place-items-center mx-auto mb-5">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-volt-600" aria-hidden="true">
              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="display text-[24px] font-semibold text-text">Request received</h1>
          <p className="text-[14.5px] text-text-3 mt-2.5 mb-7 leading-relaxed">
            Thanks for signing up. Our team reviews new accounts before activation — we’ll email
            you as soon as yours is ready. This usually takes less than one business day.
          </p>
          <Link to="/login" className="text-[14px] text-volt-600 font-medium hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen grid place-items-center bg-paper px-6 py-12">
      <div className="w-full max-w-[440px]">
        <h1 className="display text-[28px] font-semibold tracking-[-0.02em] text-text leading-[1.15] m-0">
          Create your account
        </h1>
        <p className="text-[14.5px] text-text-3 mt-2.5 mb-7 leading-relaxed">
          Tell us a bit about you. We review new accounts before activating them, then email you when
          you’re ready to send. Already have an account?{' '}
          <Link to="/login" className="text-volt-600 font-medium hover:underline">
            Sign in
          </Link>
          .
        </p>

        <form onSubmit={onSubmit} noValidate>
          {/* Honeypot: hidden from humans (off-screen, no tab stop, not
              announced to screen readers) but present in the DOM so
              form-filling bots complete it. A non-empty value makes the API
              silently drop the submission. */}
          <div
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
          >
            <label htmlFor="su-website">Website</label>
            <input
              id="su-website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              {...register('website')}
            />
          </div>

          {errorMsg && (
            <div
              role="alert"
              className="flex gap-2.5 items-start px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md mb-5"
            >
              <Icon.warn width="18" height="18" className="text-danger flex-shrink-0 mt-px" />
              <div className="text-[13px] text-danger leading-relaxed">{errorMsg}</div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="su-name" className="block text-[12px] font-medium text-text-2 mb-1.5">
                Full name
              </label>
              <Input
                id="su-name"
                type="text"
                autoComplete="name"
                placeholder="Jane Doe"
                disabled={isSubmitting}
                error={Boolean(errors.fullName)}
                {...register('fullName')}
              />
              {errors.fullName && (
                <p className="text-[12px] text-danger mt-1.5">{errors.fullName.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="su-email" className="block text-[12px] font-medium text-text-2 mb-1.5">
                Work email
              </label>
              <Input
                id="su-email"
                type="email"
                autoComplete="email"
                placeholder="jane@company.com"
                disabled={isSubmitting}
                error={Boolean(errors.email)}
                {...register('email')}
              />
              {errors.email && (
                <p className="text-[12px] text-danger mt-1.5">{errors.email.message}</p>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="su-company" className="block text-[12px] font-medium text-text-2 mb-1.5">
                Company <span className="text-text-4 font-normal">(optional)</span>
              </label>
              <Input
                id="su-company"
                type="text"
                autoComplete="organization"
                placeholder="Company name"
                disabled={isSubmitting}
                {...register('company')}
              />
            </div>
            <div>
              <label htmlFor="su-phone" className="block text-[12px] font-medium text-text-2 mb-1.5">
                Phone
              </label>
              <Input
                id="su-phone"
                type="tel"
                autoComplete="tel"
                placeholder="+1 555 123 4567"
                disabled={isSubmitting}
                error={Boolean(errors.phone)}
                {...register('phone')}
              />
              {errors.phone && (
                <p className="text-[12px] text-danger mt-1.5">{errors.phone.message}</p>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="su-password" className="block text-[12px] font-medium text-text-2 mb-1.5">
              Password
            </label>
            <Input
              id="su-password"
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              error={Boolean(errors.password)}
              {...register('password')}
            />
            {errors.password && (
              <p className="text-[12px] text-danger mt-1.5">{errors.password.message}</p>
            )}
          </div>

          <div className="mb-6">
            <label
              htmlFor="su-confirm"
              className="block text-[12px] font-medium text-text-2 mb-1.5"
            >
              Confirm password
            </label>
            <Input
              id="su-confirm"
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              error={Boolean(errors.confirmPassword)}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-[12px] text-danger mt-1.5">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner />
                <span>Submitting…</span>
              </>
            ) : (
              <span>Request my account</span>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
